import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthContext } from '../../context/AuthContext';
import { 
    UserCircleIcon, 
    ShieldCheckIcon, 
    TrashIcon,
    NoSymbolIcon,
    ClockIcon,
    XMarkIcon,
    PencilIcon,
    PlusIcon
} from '@heroicons/react/24/outline';

const AdminUserListPage = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState(''); // 'role', 'ban', 'suspend', 'edit', 'certificates'
    const [selectedUser, setSelectedUser] = useState(null);
    
    // Form states
    const [selectedRole, setSelectedRole] = useState('');
    const [banType, setBanType] = useState('temporary');
    const [banReason, setBanReason] = useState('');
    const [banDuration, setBanDuration] = useState('7');
    const [suspendReason, setSuspendReason] = useState('');
    const [suspendDuration, setSuspendDuration] = useState('3');
    
    // Edit form states
    const [editName, setEditName] = useState('');
    const [editEmail, setEditEmail] = useState('');
    const [editUsername, setEditUsername] = useState('');
    
    // Certificate states
    const [certificates, setCertificates] = useState([]);
    const [newCertTitle, setNewCertTitle] = useState('');
    const [newCertCourseId, setNewCertCourseId] = useState('');
    const [newCertCourseName, setNewCertCourseName] = useState('');
    
    const { token, user: adminUser } = useContext(AuthContext);

    const fetchUsers = useCallback(async () => {
        try {
            const config = { headers: { 'x-auth-token': token } };
            const res = await axios.get(`${API_BASE_URL}/api/admin/users`, config);
            setUsers(res.data);
        } catch (err) {
            toast.error("Failed to load users.");
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (token) fetchUsers();
    }, [token, fetchUsers]);

    const openModal = (user, type) => {
        setSelectedUser(user);
        setModalType(type);
        setSelectedRole(user.role);
        
        // Edit modal i�in verileri doldur
        if (type === 'edit') {
            setEditName(user.name || '');
            setEditEmail(user.email || '');
            setEditUsername(user.username || '');
        }
        
        // Certificates modal i�in sertifikalar� getir
        if (type === 'certificates') {
            fetchUserCertificates(user._id);
        }
        
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedUser(null);
        setModalType('');
        setBanReason('');
        setSuspendReason('');
        setEditName('');
        setEditEmail('');
        setEditUsername('');
        setCertificates([]);
        setNewCertTitle('');
        setNewCertCourseId('');
        setNewCertCourseName('');
    };

    const handleRoleChange = async () => {
        const loadingToast = toast.loading('Updating role...');
        try {
            const config = { headers: { 'x-auth-token': token } };
            await axios.put(`${API_BASE_URL}/api/admin/users/${selectedUser._id}/role`, { role: selectedRole }, config);
            toast.success('Role updated!', { id: loadingToast });
            setUsers(users.map(u => u._id === selectedUser._id ? { ...u, role: selectedRole } : u));
            closeModal();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Failed to update role.', { id: loadingToast });
        }
    };

    const handleBanUser = async () => {
        if (!banReason.trim()) {
            toast.error('Please enter a ban reason');
            return;
        }
        const loadingToast = toast.loading('Banning user...');
        try {
            const config = { headers: { 'x-auth-token': token } };
            const body = {
                banType,
                reason: banReason,
                duration: banType === 'temporary' ? banDuration : null
            };
            await axios.post(`${API_BASE_URL}/api/admin/users/${selectedUser._id}/ban`, body, config);
            toast.success('User banned!', { id: loadingToast });
            closeModal();
            fetchUsers();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Ban failed', { id: loadingToast });
        }
    };

    const handleSuspendUser = async () => {
        if (!suspendReason.trim()) {
            toast.error('Please enter suspension reason');
            return;
        }
        const loadingToast = toast.loading('Suspending user...');
        try {
            const config = { headers: { 'x-auth-token': token } };
            const body = {
                reason: suspendReason,
                duration: suspendDuration
            };
            await axios.post(`${API_BASE_URL}/api/admin/users/${selectedUser._id}/suspend`, body, config);
            toast.success('User suspended!', { id: loadingToast });
            closeModal();
            fetchUsers();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Suspension failed', { id: loadingToast });
        }
    };

    const handleDeleteUser = async (userId) => {
        if (!window.confirm('Are you sure you want to permanently delete this user?')) return;
        const loadingToast = toast.loading('Deleting user...');
        try {
            const config = { headers: { 'x-auth-token': token } };
            await axios.delete(`${API_BASE_URL}/api/admin/users/${userId}`, config);
            toast.success('User deleted!', { id: loadingToast });
            setUsers(users.filter(u => u._id !== userId));
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Failed to delete user.', { id: loadingToast });
        }
    };

    const handleEditUser = async () => {
        if (!editName.trim() || !editEmail.trim() || !editUsername.trim()) {
            toast.error('Fill in all fields');
            return;
        }
        const loadingToast = toast.loading('Updating user...');
        try {
            const config = { headers: { 'x-auth-token': token } };
            const body = {
                name: editName,
                email: editEmail,
                username: editUsername
            };
            await axios.put(`${API_BASE_URL}/api/admin/users/${selectedUser._id}`, body, config);
            toast.success('User updated!', { id: loadingToast });
            fetchUsers();
            closeModal();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Update failed', { id: loadingToast });
        }
    };

    const fetchUserCertificates = async (userId) => {
        try {
            const config = { headers: { 'x-auth-token': token } };
            const res = await axios.get(`${API_BASE_URL}/api/admin/users/${userId}/certificates`, config);
            setCertificates(res.data);
        } catch (err) {
            toast.error('Failed to load certificates');
        }
    };

    const handleAddCertificate = async () => {
        if (!newCertTitle.trim() || !newCertCourseId.trim() || !newCertCourseName.trim()) {
            toast.error('Fill in all fields');
            return;
        }
        const loadingToast = toast.loading('Adding certificate...');
        try {
            const config = { headers: { 'x-auth-token': token } };
            const body = {
                title: newCertTitle,
                courseId: newCertCourseId,
                courseName: newCertCourseName
            };
            await axios.post(`${API_BASE_URL}/api/admin/users/${selectedUser._id}/certificates`, body, config);
            toast.success('Certificate added!', { id: loadingToast });
            setNewCertTitle('');
            setNewCertCourseId('');
            setNewCertCourseName('');
            fetchUserCertificates(selectedUser._id);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Failed to add certificate', { id: loadingToast });
        }
    };

    const handleDeleteCertificate = async (certId) => {
        if (!window.confirm('Are you sure you want to delete this certificate?')) return;
        const loadingToast = toast.loading('Deleting certificate...');
        try {
            const config = { headers: { 'x-auth-token': token } };
            await axios.delete(`${API_BASE_URL}/api/admin/users/${selectedUser._id}/certificates/${certId}`, config);
            toast.success('Certificate deleted!', { id: loadingToast });
            fetchUserCertificates(selectedUser._id);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Failed to delete certificate', { id: loadingToast });
        }
    };

    if (loading) return <div className="text-center">Loading...</div>;

    return (
        <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card p-8">
            <h1 className="text-3xl font-bold mb-6">User Management</h1>
            <div className="overflow-x-auto">
                <table className="w-full text-left">
                    <thead>
                        <tr className="border-b-2 border-light-border dark:border-dark-border">
                            <th className="p-3">Name</th>
                            <th className="p-3">Email</th>
                            <th className="p-3">Role</th>
                            <th className="p-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map(user => (
                            <tr key={user._id} className="border-b border-light-border dark:border-dark-border">
                                <td className="p-3">{user.name}</td>
                                <td className="p-3">{user.email}</td>
                                <td className="p-3">
                                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                        user.role === 'admin' ? 'bg-red-500 text-white' : 
                                        user.role === 'editor' ? 'bg-orange-500 text-white' : 
                                        'bg-blue-500 text-white'
                                    }`}>
                                        {user.role}
                                    </span>
                                </td>
                                <td className="p-3 flex items-center gap-2">
                                    <button
                                        onClick={() => openModal(user, 'edit')}
                                        disabled={adminUser && user._id === adminUser._id}
                                        className="text-sm px-3 py-1 rounded bg-green-500 text-white hover:bg-green-600 disabled:opacity-50 flex items-center gap-1"
                                    >
                                        <PencilIcon className="h-4 w-4" />
                                        Edit
                                    </button>
                                    <button
                                        onClick={() => openModal(user, 'certificates')}
                                        className="text-sm px-3 py-1 rounded bg-purple-500 text-white hover:bg-purple-600 flex items-center gap-1"
                                    >
                                        <UserCircleIcon className="h-4 w-4" />
                                        Certificate
                                    </button>
                                    <button
                                        onClick={() => openModal(user, 'role')}
                                        disabled={adminUser && user._id === adminUser._id}
                                        className="text-sm px-3 py-1 rounded bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 flex items-center gap-1"
                                    >
                                        <ShieldCheckIcon className="h-4 w-4" />
                                        Role
                                    </button>
                                    <button
                                        onClick={() => openModal(user, 'ban')}
                                        disabled={adminUser && user._id === adminUser._id}
                                        className="text-sm px-3 py-1 rounded bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 flex items-center gap-1"
                                    >
                                        <NoSymbolIcon className="h-4 w-4" />
                                        Ban
                                    </button>
                                    <button
                                        onClick={() => openModal(user, 'suspend')}
                                        disabled={adminUser && user._id === adminUser._id}
                                        className="text-sm px-3 py-1 rounded bg-yellow-500 text-white hover:bg-yellow-600 disabled:opacity-50 flex items-center gap-1"
                                    >
                                        <ClockIcon className="h-4 w-4" />
                                        Suspend
                                    </button>
                                    <button
                                        onClick={() => handleDeleteUser(user._id)}
                                        disabled={adminUser && user._id === adminUser._id}
                                        className="bg-gray-700 text-white px-3 py-1 rounded text-sm hover:bg-gray-800 disabled:opacity-50 flex items-center gap-1"
                                    >
                                        <TrashIcon className="h-4 w-4" />
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Modals */}
            <AnimatePresence>
                {showModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                        onClick={closeModal}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4"
                        >
                            <div className="flex justify-between items-center mb-4">
                                                                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                                    {modalType === 'role' && 'Change Role'}
                                    {modalType === 'ban' && 'Ban User'}
                                    {modalType === 'suspend' && 'Suspend User'}
                                    {modalType === 'edit' && 'Edit User'}
                                    {modalType === 'certificates' && 'Certificate Management'}
                                </h3>
                                <button onClick={closeModal} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                                    <XMarkIcon className="h-6 w-6" />
                                </button>
                            </div>

                            {/* Role Change Modal */}
                            {modalType === 'role' && (
                                <div className="space-y-4">
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        User: <strong>{selectedUser?.username}</strong>
                                    </p>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Select New Role
                                        </label>
                                        <select
                                            value={selectedRole}
                                            onChange={(e) => setSelectedRole(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        >
                                            <option value="user">User</option>
                                            <option value="editor">Editor</option>
                                            <option value="admin">Admin</option>
                                        </select>
                                    </div>
                                    <div className="flex gap-3 mt-6">
                                        <button
                                            onClick={handleRoleChange}
                                            className="flex-1 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
                                        >
                                            Save
                                        </button>
                                        <button
                                            onClick={closeModal}
                                            className="flex-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Ban Modal */}
                            {modalType === 'ban' && (
                                <div className="space-y-4">
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        User: <strong>{selectedUser?.username}</strong>
                                    </p>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Ban Type
                                        </label>
                                        <select
                                            value={banType}
                                            onChange={(e) => setBanType(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        >
                                            <option value="temporary">Temporary Ban</option>
                                            <option value="permanent">Permanent Ban</option>
                                        </select>
                                    </div>
                                    {banType === 'temporary' && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Duration (days)
                                            </label>
                                            <input
                                                type="number"
                                                min="1"
                                                value={banDuration}
                                                onChange={(e) => setBanDuration(e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                placeholder="Number of days"
                                            />
                                        </div>
                                    )}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Reason
                                        </label>
                                        <textarea
                                            value={banReason}
                                            onChange={(e) => setBanReason(e.target.value)}
                                            rows="3"
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            placeholder="reason"
                                        />
                                    </div>
                                    <div className="flex gap-3 mt-6">
                                        <button
                                            onClick={handleBanUser}
                                            className="flex-1 bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600"
                                        >
                                            Ban
                                        </button>
                                        <button
                                            onClick={closeModal}
                                            className="flex-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Suspend Modal */}
                            {modalType === 'suspend' && (
                                <div className="space-y-4">
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        User: <strong>{selectedUser?.username}</strong>
                                    </p>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Duration (days)
                                        </label>
                                        <input
                                            type="number"
                                            min="1"
                                            value={suspendDuration}
                                            onChange={(e) => setSuspendDuration(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            placeholder="G�n say�s�"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Reason
                                        </label>
                                        <textarea
                                            value={suspendReason}
                                            onChange={(e) => setSuspendReason(e.target.value)}
                                            rows="3"
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            placeholder="reason"
                                        />
                                    </div>
                                    <div className="flex gap-3 mt-6">
                                        <button
                                            onClick={handleSuspendUser}
                                            className="flex-1 bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600"
                                        >
                                            Suspend
                                        </button>
                                        <button
                                            onClick={closeModal}
                                            className="flex-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Edit User Modal */}
                            {modalType === 'edit' && (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Name Surname
                                        </label>
                                        <input
                                            type="text"
                                            value={editName}
                                            onChange={(e) => setEditName(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            placeholder="Name Surname"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Username
                                        </label>
                                        <input
                                            type="text"
                                            value={editUsername}
                                            onChange={(e) => setEditUsername(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            placeholder="Username"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Email
                                        </label>
                                        <input
                                            type="email"
                                            value={editEmail}
                                            onChange={(e) => setEditEmail(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            placeholder="Email"
                                        />
                                    </div>
                                    <div className="flex gap-3 mt-6">
                                        <button
                                            onClick={handleEditUser}
                                            className="flex-1 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600"
                                        >
                                            Save
                                        </button>
                                        <button
                                            onClick={closeModal}
                                            className="flex-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Certificates Modal */}
                            {modalType === 'certificates' && (
                                <div className="space-y-4">
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        User: <strong>{selectedUser?.username}</strong>
                                    </p>
                                    
                                    {/* Certificate List */}
                                    <div className="max-h-60 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg p-3">
                                        <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">Existing Certificates</h4>
                                        {certificates.length === 0 ? (
                                            <p className="text-sm text-gray-500 dark:text-gray-400">No certificates found</p>
                                        ) : (
                                            <div className="space-y-2">
                                                {certificates.map(cert => (
                                                    <div key={cert._id} className="flex justify-between items-center p-2 bg-gray-100 dark:bg-gray-700 rounded">
                                                        <div>
                                                            <p className="font-medium text-sm text-gray-900 dark:text-white">{cert.title}</p>
                                                            <p className="text-xs text-gray-600 dark:text-gray-400">{cert.courseName}</p>
                                                        </div>
                                                        <button
                                                            onClick={() => handleDeleteCertificate(cert._id)}
                                                            className="text-red-500 hover:text-red-700"
                                                        >
                                                            <TrashIcon className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Yeni Sertifika Ekle */}
                                    <div className="border-t border-gray-300 dark:border-gray-600 pt-4">
                                        <h4 className="font-semibold mb-2 text-gray-900 dark:text-white flex items-center gap-2">
                                            <PlusIcon className="h-5 w-5" />
                                            Add New Certificate
                                        </h4>
                                        <div className="space-y-3">
                                            <input
                                                type="text"
                                                value={newCertTitle}
                                                onChange={(e) => setNewCertTitle(e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                                placeholder="Certificate Title"
                                            />
                                            <input
                                                type="text"
                                                value={newCertCourseName}
                                                onChange={(e) => setNewCertCourseName(e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                                placeholder="Course Name"
                                            />
                                            <input
                                                type="text"
                                                value={newCertCourseId}
                                                onChange={(e) => setNewCertCourseId(e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                                placeholder="Course ID"
                                            />
                                            <button
                                                onClick={handleAddCertificate}
                                                className="w-full bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 flex items-center justify-center gap-2"
                                            >
                                                <PlusIcon className="h-5 w-5" />
                                                Add Certificate
                                            </button>
                                        </div>
                                    </div>

                                    <button
                                        onClick={closeModal}
                                        className="w-full bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 mt-4"
                                    >
                                        Close
                                    </button>
                                </div>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default AdminUserListPage;
